// Atelier de peinture (cosmétiques refaits, choix de Léni du 4 novembre 2026) : chaque pièce du tank prend une couleur
// libre, une matière, un motif et un autocollant ; s'y ajoutent aura, traînée et effets de tir, d'élimination et de mort.
// Coffres en pièces (probabilités affichées) et achat direct en gemmes, sans hasard. L'aperçu montre le résultat en
// direct, effets compris ; un objet non possédé peut être essayé, mais n'est pas enregistré.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getClass } from '../../../shared/catalog/classes';
import { brightEnough,
  AURAS,
  CHESTS,
  DEATHS,
  DECALS,
  DECAL_PARTS,
  DEFAULT_LOOK,
  FREE_COSMETICS,
  KILLS,
  MATERIALS,
  PARTS,
  PATTERNS,
  RARITIES,
  RARITY_COLOR,
  SHOTS,
  TRAILS,
  lookKey,
  type ChestDef,
  type CosmeticDef,
  type CosmeticSlot,
  type PartId,
  type Rarity,
  type TankLook,
} from '../../../shared/cosmetics';
import { COSMETICS } from '../../../shared/shop';
import { apiGet, apiPost, type MeResponse } from '../game/api';
import { css } from '../game/colors';
import { formatNumber, formatPercent, t, type Key } from '../i18n';
import { Icon } from './icons';
import type { Page } from './MenuShell';
import { SwatchImage, TankImage, TankStageView } from './TankPreview';
import type { TankStage } from './tankArt';

interface LookData {
  look: TankLook;
  saved: boolean;
  owned: string[];
  chests: ChestDef[];
  refunds: Record<Rarity, number>;
  gemPrices: Record<Rarity, number>;
  history: { chest: string; item: string; rarity: Rarity; duplicate: boolean; refund: number; at: string }[];
  balances: Record<string, number>;
  models: string[];
}

type Tab = 'paint' | 'fx' | 'chests' | 'buy';
const FX_SLOTS: { slot: CosmeticSlot; list: CosmeticDef[] }[] = [
  { slot: 'aura', list: AURAS },
  { slot: 'trail', list: TRAILS },
  { slot: 'shot', list: SHOTS },
  { slot: 'kill', list: KILLS },
  { slot: 'death', list: DEATHS },
];
const SWATCHES = [
  0x8b97a5, 0x5f6976, 0x3a414b, 0x1c1f24, 0xeef3f8, 0xc0392b, 0xe74c3c, 0xd35400, 0xf39c12, 0xf1c40f, 0x27ae60, 0x16a085, 0x00d2ff, 0x2e86de,
  0x3d5a80, 0x8e44ad, 0xc77dff, 0xff4f7b, 0x7f5539, 0xc4a574,
];

export const cosName = (id: string) => t(`cos_${id.replace(':', '_')}` as Key);
export const rarityName = (r: Rarity) => t(`rarity_${r}` as Key);
const hex = (c: number) => `#${c.toString(16).padStart(6, '0')}`;
const reqId = () => (crypto.randomUUID?.() ?? `${Date.now()}${Math.random()}`).replace(/[^A-Za-z0-9]/g, '').slice(0, 32);

/** Bouton d'achat en deux temps : un premier clic arme, un second confirme dans les 4 s (pas de double clic). */
function ConfirmButton({ label, confirm, onConfirm, disabled }: { label: string; confirm: string; onConfirm: () => Promise<void>; disabled?: boolean }) {
  const [armed, setArmed] = useState(0);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const id = setTimeout(() => setArmed(0), 4000);
    return () => clearTimeout(id);
  }, [armed]);
  return (
    <button
      type="button"
      className={`btn btn-small ${armed ? 'btn-primary' : ''}`}
      disabled={disabled || busy}
      aria-busy={busy}
      onClick={(e) => {
        if (!armed) return setArmed(e.timeStamp);
        if (e.timeStamp - armed < 350) return; // un double clic ne confirme pas
        setArmed(0);
        setBusy(true);
        void onConfirm().finally(() => setBusy(false));
      }}
    >
      {armed ? confirm : label}
    </button>
  );
}

function RarityTag({ r }: { r: Rarity }) {
  return (
    <span className="rarity-tag" style={{ color: css(RARITY_COLOR[r]) }}>
      {rarityName(r)}
    </span>
  );
}

export function PaintPage({ me, onChanged, go }: { me: MeResponse | null; onChanged: () => void; go: (p: Page) => void }) {
  const account = me?.account ?? null;
  const base = getClass('BASE');
  const [data, setData] = useState<LookData | null>(null);
  const [look, setLook] = useState<TankLook>(DEFAULT_LOOK);
  const [savedKey, setSavedKey] = useState(lookKey(DEFAULT_LOOK));
  const [tab, setTab] = useState<Tab>('paint');
  const [part, setPart] = useState<PartId>('hull');
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const [reveal, setReveal] = useState<LookData['history'][number] | null>(null);
  const stage = useRef<TankStage | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await apiGet<LookData>('/api/look');
      setData(d);
      setLook(d.look);
      setSavedKey(lookKey(d.look));
    } catch {
      /* invité ou panne : l'atelier reste en lecture */
    }
  }, []);
  useEffect(() => {
    if (account) void load();
  }, [account?.publicId, load]);

  const owned = useMemo(() => new Set([...FREE_COSMETICS, ...(data?.owned ?? [])]), [data]);
  const stageLook = useMemo(() => ({ painted: look }), [lookKey(look)]);
  const dirty = lookKey(look) !== savedKey;
  const unowned = useMemo(() => {
    const ids = new Set<string>();
    for (const p of PARTS) for (const id of [look.parts[p].mat, look.parts[p].pat, look.parts[p].dec]) if (id && !owned.has(id)) ids.add(id);
    for (const s of ['aura', 'trail', 'shot', 'kill', 'death'] as const) if (look[s] && !owned.has(look[s]!)) ids.add(look[s]!);
    return [...ids];
  }, [look, owned]);

  // Couleur trop sombre éclaircie tout de suite, comme le fera le serveur (brightEnough).
  const setPaint = (patch: Partial<TankLook['parts'][PartId]>) =>
    setLook((l) => ({ ...l, parts: { ...l.parts, [part]: { ...l.parts[part], ...patch, ...(patch.color !== undefined ? { color: brightEnough(patch.color, part) } : {}) } } }));
  const setFx = (slot: CosmeticSlot, id: string | null) => setLook((l) => ({ ...l, [slot]: id }));
  const fail = (r: string) =>
    setMsg({ text: t(r === 'funds' ? 'paint_err_funds' : r === 'earned' ? 'paint_err_earned' : r === 'verify' ? 'paint_err_verify' : r === 'frozen' ? 'paint_err_frozen' : 'paint_err'), ok: false });

  const save = async () => {
    const r = await apiPost<{ look: TankLook }>('/api/look', { look });
    if (r.status !== 200) return fail('error');
    setLook(r.data.look);
    setSavedKey(lookKey(r.data.look));
    setMsg({ text: t('paint_saved'), ok: true });
    setData((d) => (d ? { ...d, saved: true } : d));
  };
  /** Retour au dessin classique (styles et skins de classe) : l'apparence enregistrée est effacée. */
  const classic = async () => {
    const r = await apiPost<{ look: null }>('/api/look', { look: null });
    if (r.status !== 200) return fail('error');
    setData((d) => (d ? { ...d, saved: false } : d));
    setMsg({ text: t('paint_classicDone'), ok: true });
  };
  const openChest = async (c: ChestDef) => {
    setMsg(null);
    const r = await apiPost<{ result: string; item?: string; rarity?: Rarity; duplicate?: boolean; refund?: number }>('/api/chest/open', { chest: c.id, requestId: reqId() });
    if (r.data.result !== 'ok' || !r.data.item) return fail(r.data.result);
    setReveal({ chest: c.id, item: r.data.item, rarity: r.data.rarity!, duplicate: !!r.data.duplicate, refund: r.data.refund ?? 0, at: new Date().toISOString() });
    await load();
    onChanged();
  };
  const buy = async (d: CosmeticDef) => {
    setMsg(null);
    const r = await apiPost<{ result: string }>('/api/cosmetic/buy', { itemId: d.id, requestId: reqId() });
    if (r.data.result !== 'ok') return fail(r.data.result);
    setMsg({ text: t('paint_bought', { name: cosName(d.id) }), ok: true });
    await load();
    onChanged();
  };

  if (!account) {
    return (
      <div className="page paint-page">
        <section className="plate">
          <h1 className="plate-title">{t('paint_title')}</h1>
          <p>{t('paint_needAccount')}</p>
          <button type="button" className="btn btn-primary" onClick={() => go('profile')}>
            {t('account')}
          </button>
        </section>
      </div>
    );
  }

  const p = look.parts[part];
  /** Vignette : un échantillon de la pièce choisie avec la matière, le motif ou l'autocollant essayé. */
  // Fond de couleur neutre : changer de couleur ne refait pas toutes les vignettes (l'aperçu montre la vraie).
  const sample = (patch: Partial<TankLook['parts'][PartId]>) => <SwatchImage paint={{ ...p, color: DEFAULT_LOOK.parts[part].color, ...patch }} size={56} />;
  const tile = (d: CosmeticDef | null, selected: boolean, onPick: () => void, art?: React.ReactNode) => {
    const have = !d || owned.has(d.id);
    return (
      <button key={d?.id ?? 'none'} type="button" className={`paint-tile ${selected ? 'selected' : ''} ${have ? '' : 'locked'}`} aria-pressed={selected} onClick={onPick} title={d ? `${cosName(d.id)} · ${rarityName(d.rarity)}` : t('paint_none')}>
        {art}
        <span className="paint-tile-name">{d ? cosName(d.id) : t('paint_none')}</span>
        {d && <RarityTag r={d.rarity} />}
        {!have && <Icon name="lock" size={12} className="icon paint-lock" />}
      </button>
    );
  };
  const swatches = (value: number, onPick: (c: number) => void, label: string) => (
    <div className="paint-swatches" role="group" aria-label={label}>
      {SWATCHES.map((c) => (
        <button key={c} type="button" className={`swatch ${c === value ? 'on' : ''}`} style={{ background: hex(c) }} aria-label={hex(c)} aria-pressed={c === value} onClick={() => onPick(c)} />
      ))}
      <label className="swatch swatch-custom" title={t('paint_customColor')}>
        <input type="color" value={hex(value)} onChange={(e) => onPick(parseInt(e.target.value.slice(1), 16))} aria-label={t('paint_customColor')} />
      </label>
    </div>
  );
  const coins = data?.balances.coins ?? me?.balances?.coins ?? 0;
  const gems = data?.balances.gems ?? me?.balances?.gems ?? 0;

  return (
    <div className="page paint-page">
      <section className="plate paint-stage-plate" aria-labelledby="paint-title">
        <h1 className="plate-title" id="paint-title">
          {t('paint_title')}
        </h1>
        <p className="wip">{t('paint_intro')}</p>
        <div className="paint-stage">
          <TankStageView cls={base} look={stageLook} radius={72} label={t('paint_title')} stageRef={stage} />
        </div>
        <div className="hud-row paint-preview-btns">
          <button type="button" className="btn btn-small btn-ghost" disabled={!look.kill} onClick={() => stage.current?.preview('kill')}>
            {t('paint_previewKill')}
          </button>
          <button type="button" className="btn btn-small btn-ghost" disabled={!look.death} onClick={() => stage.current?.preview('death')}>
            {t('paint_previewDeath')}
          </button>
        </div>
        {unowned.length > 0 && <p className="form-msg">{t('paint_tryOn', { list: unowned.map(cosName).join(', ') })}</p>}
        <div className="hud-row paint-actions">
          <button type="button" className="btn btn-primary" disabled={!dirty} onClick={() => void save()} data-testid="paint-save">
            <Icon name="check" size={16} />
            {t('paint_save')}
          </button>
          <button type="button" className="btn btn-ghost" onClick={() => setLook(DEFAULT_LOOK)}>
            {t('paint_reset')}
          </button>
          {data?.saved && (
            <button type="button" className="btn btn-ghost" onClick={() => void classic()} data-testid="paint-classic">
              {t('paint_classic')}
            </button>
          )}
        </div>
        {dirty && <p className="wip">{t('paint_unsaved')}</p>}
        <p className={msg ? `form-msg ${msg.ok ? 'ok' : ''}` : undefined} role="status" aria-live="polite">
          {msg?.text}
        </p>
      </section>

      <section className="plate paint-tools">
        <div className="seg" role="tablist">
          {(['paint', 'fx', 'chests', 'buy'] as Tab[]).map((k) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}>
              {t(`paint_tab_${k}` as Key)}
            </button>
          ))}
        </div>

        {tab === 'paint' && (
          <div className="paint-panel">
            <div className="seg paint-parts" role="group" aria-label={t('paint_parts')}>
              {PARTS.map((id) => (
                <button key={id} type="button" aria-pressed={part === id} onClick={() => setPart(id)}>
                  <i className="paint-dot" style={{ background: hex(look.parts[id].color) }} aria-hidden />
                  {t(`paint_part_${id}` as Key)}
                </button>
              ))}
            </div>
            <h3 className="eyebrow">{t('paint_color')}</h3>
            {swatches(p.color, (c) => setPaint({ color: c }), t('paint_color'))}
            <h3 className="eyebrow">{t('paint_material')}</h3>
            <div className="paint-grid">
              {MATERIALS.map((d) => tile(d, p.mat === d.id, () => setPaint({ mat: d.id }), sample({ mat: d.id, pat: null, dec: null })))}
            </div>
            <h3 className="eyebrow">{t('paint_pattern')}</h3>
            <div className="paint-grid">
              {tile(null, !p.pat, () => setPaint({ pat: null }))}
              {PATTERNS.map((d) => tile(d, p.pat === d.id, () => setPaint({ pat: d.id }), sample({ pat: d.id, dec: null })))}
            </div>
            {p.pat && (
              <>
                <h3 className="eyebrow">{t('paint_patternColor')}</h3>
                {swatches(p.patColor, (c) => setPaint({ patColor: c }), t('paint_patternColor'))}
              </>
            )}
            {DECAL_PARTS.includes(part) ? (
              <>
                <h3 className="eyebrow">{t('paint_decal')}</h3>
                <div className="paint-grid">
                  {tile(null, !p.dec, () => setPaint({ dec: null }))}
                  {DECALS.map((d) => tile(d, p.dec === d.id, () => setPaint({ dec: d.id }), sample({ dec: d.id, pat: null })))}
                </div>
                {p.dec && (
                  <>
                    <h3 className="eyebrow">{t('paint_decalColor')}</h3>
                    {swatches(p.decColor, (c) => setPaint({ decColor: c }), t('paint_decalColor'))}
                  </>
                )}
              </>
            ) : (
              <p className="wip">{t('paint_decalWhere')}</p>
            )}
            {data && data.models.length > 0 && (
              <>
                <h3 className="eyebrow">{t('paint_models')}</h3>
                <p className="wip">{t('paint_modelsHint')}</p>
                <div className="paint-grid">
                  {data.models.map((id) => {
                    const c = COSMETICS.find((x) => x.id === id);
                    if (!c?.palette) return null;
                    const pal = c.palette;
                    return (
                      <button
                        key={id}
                        type="button"
                        className="paint-tile"
                        onClick={() =>
                          setLook((l) => ({
                            ...l,
                            parts: {
                              hull: { ...l.parts.hull, color: pal.hull },
                              treads: { ...l.parts.treads, color: pal.tread },
                              turret: { ...l.parts.turret, color: pal.plate },
                              barrels: { ...l.parts.barrels, color: pal.barrel },
                            },
                          }))
                        }
                      >
                        <TankImage cls={base} look={{ palette: pal }} size={52} />
                        <span className="paint-tile-name">{c.name}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {tab === 'fx' && (
          <div className="paint-panel">
            {FX_SLOTS.map(({ slot, list }) => (
              <div key={slot}>
                <h3 className="eyebrow">{t(`paint_slot_${slot}` as Key)}</h3>
                <div className="paint-grid">
                  {tile(null, !look[slot as 'aura'], () => setFx(slot, null))}
                  {list.map((d) => tile(d, look[slot as 'aura'] === d.id, () => setFx(slot, d.id), <Icon name="star" size={22} className="icon paint-fx-icon" style={{ color: css(RARITY_COLOR[d.rarity]) }} />))}
                </div>
              </div>
            ))}
            <p className="wip">{t('paint_fxHint')}</p>
          </div>
        )}

        {tab === 'chests' && (
          <div className="paint-panel">
            <p className="wallet-line">
              {t('paint_wallet', { c: formatNumber(coins), g: formatNumber(gems) })}
            </p>
            <div className="chest-grid">
              {CHESTS.map((c) => (
                <article key={c.id} className={`chest-card chest-${c.id}`}>
                  <h3 className="chest-name">{t(`chest_${c.id}` as Key)}</h3>
                  <p className="chest-price num">{t('paint_coins', { n: formatNumber(c.coins) })}</p>
                  <table className="chest-odds">
                    <caption className="eyebrow">{t('paint_chestOdds')}</caption>
                    <tbody>
                      {RARITIES.filter((r) => c.odds[r] > 0).map((r) => (
                        <tr key={r}>
                          <th scope="row">
                            <RarityTag r={r} />
                          </th>
                          <td className="num">{formatPercent(c.odds[r] / 10)} %</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <ConfirmButton
                    label={t('paint_chestOpen')}
                    confirm={t('paint_chestConfirm', { n: formatNumber(c.coins) })}
                    disabled={coins < c.coins || !account.verified}
                    onConfirm={() => openChest(c)}
                  />
                </article>
              ))}
            </div>
            {!account.verified && <p className="form-msg">{t('paint_err_verify')}</p>}
            <p className="wip" data-testid="paint-earned">{t('paint_earnedCoins', { n: formatNumber(Math.max(0, coins - (data?.balances.coins_bought ?? 0))) })}</p>
            <p className="wip">{t('paint_dupRule', { list: RARITIES.map((r) => `${rarityName(r)} ${formatNumber(data?.refunds[r] ?? 0)}`).join(' · ') })}</p>
            {reveal && (
              <div className="chest-reveal" role="status" style={{ borderColor: css(RARITY_COLOR[reveal.rarity]) }}>
                <RarityTag r={reveal.rarity} />
                <strong>{cosName(reveal.item)}</strong>
                <span>{reveal.duplicate ? t('paint_chestDup', { n: formatNumber(reveal.refund) }) : t('paint_chestNew')}</span>
              </div>
            )}
            {data && data.history.length > 0 && (
              <>
                <h3 className="eyebrow">{t('paint_chestHistory')}</h3>
                <ul className="chest-history">
                  {data.history.map((h, i) => (
                    <li key={i}>
                      <span>{t(`chest_${h.chest}` as Key)}</span> · <strong style={{ color: css(RARITY_COLOR[h.rarity]) }}>{cosName(h.item)}</strong>
                      {h.duplicate && <span className="wip"> · +{formatNumber(h.refund)}</span>}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}

        {tab === 'buy' && (
          <div className="paint-panel">
            <p className="wallet-line">{t('paint_wallet', { c: formatNumber(coins), g: formatNumber(gems) })}</p>
            {([['mat', MATERIALS], ['pat', PATTERNS], ['dec', DECALS], ...FX_SLOTS.map((f) => [f.slot, f.list] as const)] as const).map(([slot, list]) => (
              <div key={slot}>
                <h3 className="eyebrow">{t(`paint_slot_${slot}` as Key)}</h3>
                <div className="buy-grid">
                  {list
                    .filter((d) => !FREE_COSMETICS.has(d.id))
                    .map((d) => {
                      const price = data?.gemPrices[d.rarity] ?? 0;
                      return (
                        <div key={d.id} className={`shop-card buy-card ${owned.has(d.id) ? 'owned' : ''}`}>
                          <strong>{cosName(d.id)}</strong>
                          <RarityTag r={d.rarity} />
                          {owned.has(d.id) ? (
                            <span className="wip">{t('paint_owned')}</span>
                          ) : (
                            <ConfirmButton label={t('paint_gems', { n: formatNumber(price) })} confirm={t('paint_buyConfirm', { n: formatNumber(price) })} disabled={gems < price || !account.verified} onConfirm={() => buy(d)} />
                          )}
                        </div>
                      );
                    })}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
